import { SchedulingSectionStatus } from '../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  isAppointmentService,
  isCourseService,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { BookingsApi } from '../../../api/BookingsApi';
import type { Event } from '@wix/ambassador-calendar-v3-event/types';
import {
  SESSIONS_PAGINATION_EXPERIMENT,
  SHOW_REMAINING_COURSE_SESSIONS_EXPERIMENT,
} from '../constants';

export type CourseSessionSummary = {
  totalNumberOfSessions?: number;
  remainingNumberOfSessions?: number;
};

export type ServiceSchedulingData = {
  status?: SchedulingSectionStatus;
  events?: Event[];
  nextCursor?: string;
  hasMore?: boolean;
  courseSessionSummary?: CourseSessionSummary;
};

export const getCourseSessionSummary = ({
  events,
  firstSessionStart,
  now = new Date(),
}: {
  events: Event[];
  firstSessionStart?: Date;
  now?: Date;
}): CourseSessionSummary => {
  const totalNumberOfSessions = events.length;
  const firstSessionStartTime = firstSessionStart?.getTime();
  const hasCourseStarted =
    firstSessionStartTime !== undefined &&
    !Number.isNaN(firstSessionStartTime) &&
    firstSessionStartTime <= now.getTime();

  if (!hasCourseStarted) {
    return { totalNumberOfSessions };
  }

  const nowTime = now.getTime();
  const remainingNumberOfSessions = events.reduce((total, event) => {
    const sessionEnd = event.end?.utcDate;
    if (sessionEnd) {
      return new Date(sessionEnd).getTime() > nowTime ? total + 1 : total;
    }

    const sessionStart = event.start?.utcDate;
    if (!sessionStart) {
      return total;
    }

    return new Date(sessionStart).getTime() >= nowTime ? total + 1 : total;
  }, 0);

  return { totalNumberOfSessions, remainingNumberOfSessions };
};

export async function getServiceSchedulingData({
  settings,
  bookingsApi,
  service,
  timezone,
  experiments,
}: {
  settings: any;
  service: Service;
  bookingsApi: BookingsApi;
  timezone: string;
  experiments?: { enabled: (spec: string) => boolean };
}): Promise<ServiceSchedulingData> {
  const isCourse = isCourseService(service);
  const isAppointment = isAppointmentService(service);
  const isPaginationEnabled = experiments?.enabled(
    SESSIONS_PAGINATION_EXPERIMENT,
  );
  const shouldShowRemainingCourseSessions =
    isCourse && experiments?.enabled(SHOW_REMAINING_COURSE_SESSIONS_EXPERIMENT);

  if (!isAppointment) {
    const schedule = service.schedule;
    const now = new Date();
    const from = isCourse
      ? new Date((schedule?.firstSessionStart as Date) || null)
      : new Date(now);
    let to: Date | undefined;
    if (isCourse) {
      to = new Date((schedule?.lastSessionEnd as Date) || null);
    } else if (!isPaginationEnabled) {
      to = new Date(now.setDate(now.getDate() + settings.scheduleDays));
    }
    if (from) {
      const { data } = await bookingsApi.queryEvents({
        from,
        to,
        scheduleId: schedule?.id!,
        timezone,
      });
      const nextCursor = data.pagingMetadata?.cursors?.next ?? undefined;
      const hasMore = data.pagingMetadata?.hasNext ?? false;
      const events = data.events ?? [];
      const firstSessionStart = schedule?.firstSessionStart as Date | undefined;
      const courseSessionSummary =
        shouldShowRemainingCourseSessions && !hasMore
          ? getCourseSessionSummary({ events, firstSessionStart, now })
          : undefined;

      return {
        events: data.events,
        nextCursor,
        hasMore,
        courseSessionSummary,
      };
    }
  }
  return { status: SchedulingSectionStatus.EMPTY };
}

export async function fetchMoreEvents({
  bookingsApi,
  cursor,
  scheduleId,
}: {
  bookingsApi: BookingsApi;
  cursor: string;
  scheduleId: string;
}): Promise<{ events?: Event[]; nextCursor?: string; hasMore?: boolean }> {
  const { data } = await bookingsApi.queryEventsByCursor(cursor, scheduleId);
  const nextCursor = data.pagingMetadata?.cursors?.next ?? undefined;
  const hasMore = data.pagingMetadata?.hasNext ?? false;
  return { events: data.events, nextCursor, hasMore };
}
